import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import {
  Building2,
  FileText,
  MapPin,
  Phone,
  User,
  UserCog,
  Wallet,
} from "lucide-react";
import { useUnsavedFormGuard } from "@/hooks/useUnsavedFormGuard";
import { toastApiError } from "@/lib/errorMessages";
import {
  ProfileValidationError,
  profileService,
  type UserLegalInfo,
  type UserLegalInfoUpdate,
} from "@/services/profileService";
import { ProfileType } from "@/types";
import { preventImplicitSubmitOnEnter } from "@/lib/formKeyboard";
import {
  buildProfileFormDefaults,
  hasTypeSpecificProfileData,
} from "./profileDetailForm";
import { type DirtyFieldMap, type ProfileFormValues } from "./profileFormTypes";
import {
  createProfileSectionSchema,
  getProfileSectionFields,
  isProfileSectionDirty,
  pickSectionFormValues,
  type ProfileSectionId,
} from "./profileSections";
import { CompanyFormSection } from "./CompanyFormSection";
import { ContactSection } from "./ContactSection";
import { PaymentSection } from "./PaymentSection";
import { PersonFormSection } from "./PersonFormSection";
import { ProfileTypeSelector } from "./ProfileTypeSelector";
import {
  ProfileSectionNav,
  type ProfileSectionNavItem,
} from "./ProfileSectionNav";
import type { ProfileSectionControls } from "./profileSectionTypes";

interface ProfileSettingsFormProps {
  profile: UserLegalInfo;
  userEmail: string;
  userId: string;
  onProfileUpdated: (profile: UserLegalInfo) => void;
  /** Settings page: side section nav (xl) + chip row; off when embedded in admin legal tab. */
  showSectionNav: boolean;
}

const sectionAnchorId = (sectionId: ProfileSectionId | "account") =>
  `profile-section-${sectionId}`;

export function ProfileSettingsForm({
  profile,
  userEmail,
  userId,
  onProfileUpdated,
  showSectionNav,
}: ProfileSettingsFormProps) {
  const { t } = useTranslation("settings");
  const { t: tVal } = useTranslation("validation");
  const { t: tCommon } = useTranslation("common");

  const [profileType, setProfileType] = useState<ProfileType>(
    profile.profile_type,
  );
  const [savingSection, setSavingSection] = useState<ProfileSectionId | null>(
    null,
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingProfileType, setPendingProfileType] =
    useState<ProfileType | null>(null);
  const [isChangingType, setIsChangingType] = useState(false);
  const [addressSectionTokens, setAddressSectionTokens] = useState({
    registered: 0,
  });

  const defaultValues = useMemo(
    () => buildProfileFormDefaults(profile, userEmail),
    [profile, userEmail],
  );

  const {
    register,
    control,
    getValues,
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<ProfileFormValues>({
    defaultValues,
  });

  useUnsavedFormGuard(isDirty);

  const dirty = dirtyFields as DirtyFieldMap;

  const bumpAddressSectionToken = useCallback((sectionId: ProfileSectionId) => {
    if (
      sectionId === "person-residence" ||
      sectionId === "company-registered"
    ) {
      setAddressSectionTokens((prev) => ({
        ...prev,
        registered: prev.registered + 1,
      }));
    }
  }, []);

  const applySectionBaseline = useCallback(
    (
      sectionId: ProfileSectionId,
      source: UserLegalInfo,
      dirtySnapshot: DirtyFieldMap,
    ) => {
      const baseline = buildProfileFormDefaults(source, userEmail);

      if (source.profile_completed) {
        reset(baseline);
        clearErrors();
        setAddressSectionTokens((prev) => ({
          registered: prev.registered + 1,
        }));
        return;
      }

      const sectionFields = new Set(
        getProfileSectionFields(sectionId, source.field_policy),
      );
      const currentValues = getValues();
      const dirtyValuesOutsideSection = (
        Object.entries(dirtySnapshot) as Array<
          [keyof ProfileFormValues, boolean | undefined]
        >
      ).filter(
        ([field, isDirty]) => Boolean(isDirty) && !sectionFields.has(field),
      );

      reset(baseline, { keepErrors: true });

      for (const [field] of dirtyValuesOutsideSection) {
        setValue(field, currentValues[field], { shouldDirty: true });
      }
      clearErrors([...getProfileSectionFields(sectionId, source.field_policy)]);
      bumpAddressSectionToken(sectionId);
    },
    [
      reset,
      getValues,
      setValue,
      clearErrors,
      userEmail,
      bumpAddressSectionToken,
    ],
  );

  const revertSection = useCallback(
    (sectionId: ProfileSectionId) => {
      applySectionBaseline(sectionId, profile, dirtyFields as DirtyFieldMap);
    },
    [applySectionBaseline, profile, dirtyFields],
  );

  const saveSection = useCallback(
    async (sectionId: ProfileSectionId) => {
      if (!isProfileSectionDirty(sectionId, dirtyFields as DirtyFieldMap)) {
        return;
      }

      const sectionValues = pickSectionFormValues(
        getValues(),
        sectionId,
        profile.field_policy,
      );
      const schema = createProfileSectionSchema(sectionId, tVal, {
        fieldPolicy: profile.field_policy,
      });
      const parsed = schema.safeParse(sectionValues);

      clearErrors([
        ...getProfileSectionFields(sectionId, profile.field_policy),
      ]);

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const field = issue.path[0];
          if (typeof field === "string" && field in getValues()) {
            setError(field as keyof ProfileFormValues, {
              type: "client",
              message: issue.message,
            });
          }
        }
        toast.error(t("checkHighlighted"));
        return;
      }

      setSavingSection(sectionId);
      try {
        const profileUpdate: UserLegalInfoUpdate = {
          ...(parsed.data as UserLegalInfoUpdate),
        };

        const updated = await profileService.updateProfile(
          userId,
          profileUpdate,
        );
        onProfileUpdated(updated);
        applySectionBaseline(sectionId, updated, dirtyFields as DirtyFieldMap);
        toast.success(t("sectionSaved"));
      } catch (err) {
        if (err instanceof ProfileValidationError && err.errors.length > 0) {
          for (const validationError of err.errors) {
            if (!(validationError.field in getValues())) {
              continue;
            }
            setError(validationError.field as keyof ProfileFormValues, {
              type: "server",
              message: validationError.message,
            });
          }
          toast.error(t("checkHighlighted"));
        } else {
          toastApiError(err, "saveProfile");
        }
      } finally {
        setSavingSection(null);
      }
    },
    [
      dirtyFields,
      getValues,
      tVal,
      clearErrors,
      setError,
      t,
      userId,
      onProfileUpdated,
      applySectionBaseline,
      profile.field_policy,
    ],
  );

  const sectionControls = useCallback(
    (sectionId: ProfileSectionId): ProfileSectionControls => ({
      anchorId: sectionAnchorId(sectionId),
      modified: isProfileSectionDirty(sectionId, dirty),
      isSaving: savingSection === sectionId,
      onSave: () => {
        void saveSection(sectionId);
      },
      onRevert: () => {
        revertSection(sectionId);
      },
    }),
    [dirty, savingSection, saveSection, revertSection],
  );

  const applyProfileTypeChange = useCallback(
    async (newType: ProfileType) => {
      setIsChangingType(true);
      try {
        const updated = await profileService.changeProfileType(
          userId,
          newType,
          true,
        );
        onProfileUpdated(updated);
        setProfileType(newType);
        reset(buildProfileFormDefaults(updated, userEmail));
        setAddressSectionTokens((prev) => ({
          registered: prev.registered + 1,
        }));
        toast.success(
          t("profileChangedTo", {
            type: t(newType === ProfileType.PERSON ? "individual" : "company"),
          }),
        );
      } catch (error) {
        toastApiError(error, "changeProfileType");
      } finally {
        setIsChangingType(false);
        setShowConfirmModal(false);
        setPendingProfileType(null);
      }
    },
    [userId, onProfileUpdated, reset, userEmail, t],
  );

  const handleProfileTypeChange = (newType: ProfileType) => {
    if (newType === profileType) return;

    if (hasTypeSpecificProfileData(profile, profileType)) {
      setPendingProfileType(newType);
      setShowConfirmModal(true);
      return;
    }

    void applyProfileTypeChange(newType);
  };

  const confirmProfileTypeChange = () => {
    if (!pendingProfileType) return;
    void applyProfileTypeChange(pendingProfileType);
  };

  const personControls = {
    personal: sectionControls("person-personal"),
    residence: sectionControls("person-residence"),
    identity: sectionControls("person-identity"),
  };

  const companyControls = {
    business: sectionControls("company-business"),
    registered: sectionControls("company-registered"),
    legalRep: sectionControls("company-legal-rep"),
  };

  const profileBaseKey = profile.id;
  const registeredAddressSyncKey = `registered-${profileBaseKey}-${addressSectionTokens.registered}`;

  const isPerson = profileType === ProfileType.PERSON;
  const navItems: ProfileSectionNavItem[] = [
    {
      anchorId: sectionAnchorId("account"),
      label: t("forms.profileType.cardTitle"),
      icon: UserCog,
      modified: false,
    },
    ...(isPerson
      ? [
          {
            id: "person-personal" as const,
            label: t("forms.person.cardPersonal"),
            icon: User,
          },
          {
            id: "person-residence" as const,
            label: t("forms.person.cardResidence"),
            icon: MapPin,
          },
          {
            id: "person-identity" as const,
            label: t("forms.person.cardIdentity"),
            icon: FileText,
          },
        ]
      : [
          {
            id: "company-business" as const,
            label: t("forms.company.cardBusiness"),
            icon: Building2,
          },
          {
            id: "company-registered" as const,
            label: t("forms.company.cardRegistered"),
            icon: MapPin,
          },
          {
            id: "company-legal-rep" as const,
            label: t("forms.company.cardLegalRep"),
            icon: User,
          },
        ]
    ).map((item) => ({
      anchorId: sectionAnchorId(item.id),
      label: item.label,
      icon: item.icon,
      modified: isProfileSectionDirty(item.id, dirty),
    })),
    {
      anchorId: sectionAnchorId("contact"),
      label: t("forms.contact.cardTitle"),
      icon: Phone,
      modified: isProfileSectionDirty("contact", dirty),
    },
    {
      anchorId: sectionAnchorId("payment"),
      label: t("forms.payment.cardTitle"),
      icon: Wallet,
      modified: isProfileSectionDirty("payment", dirty),
    },
  ];

  const sections = (
    <div className="min-w-0 space-y-5 pb-8">
      <ProfileTypeSelector
        anchorId={sectionAnchorId("account")}
        regionCode={profile.region_code}
        value={profileType}
        onChange={handleProfileTypeChange}
      />

      {isPerson ? (
        <PersonFormSection
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          profileSyncKey={profileBaseKey}
          registeredAddressSyncKey={registeredAddressSyncKey}
          sections={personControls}
        />
      ) : (
        <CompanyFormSection
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          profileSyncKey={`${profileBaseKey}-${profileType}`}
          registeredAddressSyncKey={registeredAddressSyncKey}
          sections={companyControls}
        />
      )}

      <ContactSection
        control={control}
        register={register}
        watch={watch}
        errors={errors}
        userId={userId}
        savedPhone={profile.contact_phone}
        savedEmail={profile.contact_email}
        phoneDirty={Boolean(dirty.contact_phone)}
        emailDirty={Boolean(dirty.contact_email)}
        phoneVerified={profile.contact_phone_verified}
        emailVerified={profile.contact_email_verified}
        fieldPolicy={profile.field_policy}
        section={sectionControls("contact")}
        onContactVerified={onProfileUpdated}
      />

      <PaymentSection
        register={register}
        errors={errors}
        fieldPolicy={profile.field_policy}
        section={sectionControls("payment")}
      />
    </div>
  );

  return (
    <div onKeyDown={preventImplicitSubmitOnEnter}>
      {showSectionNav ? (
        <div className="grid items-start gap-5 xl:grid-cols-[220px_minmax(0,1fr)] xl:gap-8">
          <div className="min-w-0 xl:sticky xl:top-24">
            <ProfileSectionNav
              items={navItems}
              profileCompleted={profile.profile_completed}
            />
          </div>
          {sections}
        </div>
      ) : (
        sections
      )}

      <ConfirmModal
        closeLabel={tCommon("close")}
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingProfileType(null);
        }}
        onConfirm={confirmProfileTypeChange}
        title={t("confirmChangeTitle")}
        message={
          pendingProfileType
            ? t("confirmChangeMessage", {
                from: t(
                  profileType === ProfileType.PERSON ? "individual" : "company",
                ),
                to: t(
                  pendingProfileType === ProfileType.PERSON
                    ? "individual"
                    : "company",
                ),
                dataType: t(
                  profileType === ProfileType.PERSON
                    ? "personalData"
                    : "companyData",
                ),
              })
            : ""
        }
        confirmText={t("confirmChange")}
        cancelText={tCommon("cancel")}
        isDestructive
        isLoading={isChangingType}
      />
    </div>
  );
}
